import { Box, Button, Link, ListItem, OrderedList, Text, UnorderedList } from "@chakra-ui/react";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";

/*
  Public account & data deletion information page.

  This page is deliberately OUTSIDE ProtectedRoute and outside both prelaunch
  route blocks, so it is reachable by anyone - including users who have already
  uninstalled the app, and Google Play reviewers who have no account.

  Google Play's User Data policy requires a web resource that states the steps to
  request deletion, which data is deleted, which data is retained, and for how
  long. Deleting an account still requires signing in and confirming an emailed
  code - that flow lives in pages/Dashboard/AccountDeletion and is unchanged.
  Nothing on this page performs a deletion.
*/

const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL ?? "";

const appleCancelUrl = "https://support.apple.com/HT202039";
const googleCancelUrl =
  "https://support.google.com/googleplay/answer/7018481?hl=en&co=GENIE.Platform%3DAndroid";

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <Text fontSize="22px" fontWeight="bold" mt="8" mb="3">
    {children}
  </Text>
);

const DataDeletion = () => {
  const navigate = useNavigate();
  const isLoggedIn = Boolean(localStorage.getItem("token"));

  return (
    <MainLayout>
      <Box
        maxW="760px"
        mx="auto"
        my="8"
        px={["6", "10"]}
        py="10"
        bg="white"
        rounded="16px"
        boxShadow="sm"
        color="gray.800"
      >
        <Text fontSize="30px" fontWeight="bold" mb="2">
          Delete your Shakoo Maku account and data
        </Text>
        <Text fontSize="16px" color="gray.600">
          This page explains how to permanently delete your Shakoo Maku account,
          what happens to your data, and what is kept.
        </Text>

        <SectionHeading>How to request deletion</SectionHeading>
        <OrderedList spacing="2" fontSize="16px">
          <ListItem>
            Sign in to your account at{" "}
            <Link href="/login" color="blue.600" textDecoration="underline">
              account.shakoomaku.com/login
            </Link>
            .
          </ListItem>
          <ListItem>Go to Account Deletion from your account settings.</ListItem>
          <ListItem>
            We email a 6-digit confirmation code to your registered address. Enter
            it to confirm you own the account.
          </ListItem>
          <ListItem>
            Re-enter your email and password to confirm. Your account and data are
            then deleted immediately.
          </ListItem>
        </OrderedList>
        <Text fontSize="15px" color="gray.600" mt="3">
          You do not need the app installed to delete your account. If you can no
          longer sign in,{" "}
          {supportEmail ? (
            <Link
              href={`mailto:${supportEmail}?subject=Account deletion request`}
              color="blue.600"
              textDecoration="underline"
            >
              email us at {supportEmail}
            </Link>
          ) : (
            "contact us"
          )}{" "}
          and we will process the request manually.
        </Text>

        <SectionHeading>What is deleted</SectionHeading>
        <Text fontSize="16px" mb="2">
          The following is erased from our systems immediately and permanently:
        </Text>
        <UnorderedList spacing="2" fontSize="16px">
          <ListItem>
            Your account record, including your email address and password
          </ListItem>
          <ListItem>
            All profile data, including account information, child profiles and
            names, and learning progress
          </ListItem>
          <ListItem>
            Google Play and Apple purchase receipts stored by Shakoo Maku
          </ListItem>
          <ListItem>
            Your customer record with our payment processor, after any active
            subscription is cancelled
          </ListItem>
        </UnorderedList>

        <SectionHeading>What is retained</SectionHeading>
        <Text fontSize="16px" mb="2">
          Nothing is retained on Shakoo Maku's systems. Some records are held by
          third parties and cannot be deleted by us:
        </Text>
        <UnorderedList spacing="2" fontSize="16px">
          <ListItem>
            Our payment processor retains payment and transaction records to meet
            their own financial and tax obligations.
          </ListItem>
          <ListItem>
            Apple and Google retain purchase history in your own App Store or
            Google Play account. You manage these through your store account.
          </ListItem>
        </UnorderedList>

        <SectionHeading>Retention period</SectionHeading>
        <Text fontSize="16px">
          None. Deletion happens at the time you confirm it, and we do not keep a
          copy of your account or profile data afterwards.
        </Text>

        <SectionHeading>Before you delete</SectionHeading>
        <Text fontSize="16px" mb="2">
          Deletion is irreversible. You will lose all progress, and you will not
          be refunded for any remaining subscription term or past purchases.
        </Text>
        <Text fontSize="16px">
          If you subscribed through Apple or Google rather than through our
          website, deleting your account here does not cancel that subscription.
          Cancel it in the relevant store as well:
        </Text>
        <Box mt="3">
          <Link href={appleCancelUrl} isExternal color="blue.600" textDecoration="underline">
            Cancel an Apple subscription
          </Link>
          <br />
          <Link href={googleCancelUrl} isExternal color="blue.600" textDecoration="underline">
            Cancel a Google Play subscription
          </Link>
        </Box>

        {/* Only show the actual delete action to a logged-in user. Anyone else
            (including uninstalled users and Play reviewers) sees a sign-in prompt.
            The deletion itself still requires the emailed code + password on the
            /account-deletion page. */}
        {isLoggedIn ? (
          <Button
            variant="primary"
            mt="10"
            px="8"
            onClick={() => navigate("/account-deletion")}
          >
            Delete my account
          </Button>
        ) : (
          <Button
            variant="primary"
            mt="10"
            px="8"
            onClick={() => navigate("/login")}
          >
            Sign in to delete my account
          </Button>
        )}
      </Box>
    </MainLayout>
  );
};

export default DataDeletion;
